import React from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";

const teamMembers = [
  {
    id: "shivdeep-mehta",
    name: "Shivdeep Mehta",
    role: (
      <>
        Barrister & Solicitor
        <br />
      </>
    ),
    title: "Founding Lawyer",
    image: "/images/lawyer.jpeg",
    bio: [
      "Shivdeep Mehta, a dual qualified lawyer, is a passionate and community-focused lawyer with international legal experience and a strong commitment to access to justice. Before founding Falcon Law, Shivdeep completed his articles at a prominent family law firm in downtown Calgary. Shivdeep brings to his practice a strong foundation in both family and immigration law, combined with deep empathy and insight into the lived realities of immigrant families. Having personally experienced the immigration process, Shivdeep offers a nuanced understanding of the cultural, emotional, and financial challenges his clients often face.",
      "Fluent in four languages, Shivdeep communicates effectively with a broad range of clients and is known for his calm, respectful, and compassionate approach. He excels in navigating high-conflict matters and crafting thoughtful legal strategies tailored to each client’s needs.",
      "Shivdeep has consistently strived to ensure that quality legal support is practical, culturally aware, and rooted in empathy. His goal is to build a practice that empowers individuals and families with clear, compassionate legal guidance. Outside of work, Shivdeep enjoys exploring new restaurants, traveling, bourbon hunting, and trap shooting at the range."
    ],
    areasOfPractice: ["Family Law", "Wills and Estate", "Real Estate", "Immigration Law"],
    memberships: [
      "Law Society of Alberta",
      "Canadian Bar Association",
      "Calgary Bar Association",
      "The South Asian Bar Association"
    ],
    email: "smehta@falconlawoffice.ca",
    phone: "(587) 971-0447",
    Fax: "(587) 791-1838"
  },
  {
    id: "sakshi-doda",
    name: "Sakshi Doda",
    role: <>Barrister & Solicitor</>,
    title: "Managing Lawyer",
    image: "/images/sakshii.jpg",
    bio: [
      "Sakshi is a dual qualified lawyer who finds fulfilment in assisting clients across a range of civil litigation matters including personal injury, employment law, administrative law and residential tenancy disputes. Sakshi has appeared before the Alberta Court of Justice, the Court of King’s Bench in Alberta and Administrative Tribunals.",
      "Sakshi believes in assisting clients with a cost-conscious approach, focusing on providing practical solutions to resolve legal disputes efficiently.",
      "Fluency in three languages enables Sakshi to serve a diverse clientele with confidence, ensuring trust, clarity and effective communication with clients. Outside her professional life, Sakshi enjoys travelling, hiking, cycling, nature walks, yoga, reading non-fiction and spending time with her family and friends."
    ],
    areasOfPractice: ["Civil Litigation", "Personal Injury", "Employment Law", "Commercial Law"],
    memberships: [
      "Canadian Bar Association",
      "Calgary Bar Association",
      "South Asian Bar Association",
      "Association of Women Lawyers"
    ],
    email: "sdoda@falconlawoffice.ca",
    phone: "(431) 777-5078",
    Fax: "(587) 791-1838"
  }
];

const textVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.18 + 0.4, duration: 0.8, ease: "easeOut" }
  })
};

export default function MemberProfile() {
  const { id } = useParams();
  const member = teamMembers.find((m) => m.id === id);

  if (!member) {
    return (
      <div className="text-center py-20 text-2xl text-red-500 font-semibold">
        Team member not found.
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex justify-center px-6 py-16">
      <div className="max-w-6xl w-full flex flex-col lg:flex-row gap-16">
        {/* Left: Image and contact details */}
        <div className="flex flex-col items-center lg:items-start w-full lg:w-[420px]">
          {/* Large profile image */}
          <img
            src={member.image}
            alt={member.name}
            className="rounded-2xl shadow-2xl w-[340px] h-[420px] object-cover mb-8"
            style={{ objectPosition: "top center" }}
          />

          {/* Contact Card */}
          <div className="w-full mt-2 space-y-5 text-lg">
            {/* Email */}
            <div className="flex items-center text-gray-700 gap-3">
              <img
                src="/images/email.png"
                alt="Email"
                className="w-6 h-6 shrink-0"
              />
              <a
                href={`mailto:${member.email}`}
                className="break-all underline text-blue-700 hover:text-blue-900 transition"
              >
                {member.email}
              </a>
            </div>

            {/* Phone */}
            <div className="flex items-center text-gray-700 gap-3">
              <img
                src="/images/phone.png"
                alt="Phone"
                className="w-6 h-6 shrink-0"
              />
              <a href={`tel:${member.phone.replace(/[^\d+]/g, "")}`} className="hover:text-blue-900">
                {member.phone}
              </a>
            </div>

            {/* Fax */}
            <div className="flex items-center text-gray-700 gap-3">
              <img
                src="/images/print.png"
                alt="Fax"
                className="w-6 h-6 shrink-0"
              />
              <span>{member.Fax}</span>
            </div>
          </div>
        </div>

        {/* Right: Main Info */}
        <div className="flex-1">
          {/* Name and title */}
          <div className="mb-3">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-blue-900 mb-2">
              {member.name}
            </h1>
            <div className="text-2xl font-bold text-yellow-600 mb-1">
              {member.title}
            </div>
            <div className="text-xl text-yellow-600 mb-7">{member.role}</div>
          </div>

          {/* Bio */}
          <div className="space-y-6 text-gray-900 text-xl leading-relaxed">
            {member.bio.map((p, i) => (
              <motion.p
                key={i}
                variants={textVariants}
                initial="hidden"
                animate="visible"
                custom={i}
              >
                {p}
              </motion.p>
            ))}
          </div>

          {/* Areas and memberships */}
          <div className="mt-12 flex flex-col sm:flex-row gap-12">
            <div>
              <div className="font-extrabold text-gray-900 text-lg mb-2">
                Areas of Practice
              </div>
              <ul className="list-disc ml-6 text-gray-800 text-lg">
                {member.areasOfPractice.map((area, i) => (
                  <li key={i}>{area}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-extrabold text-gray-900 text-lg mb-2">
                Memberships
              </div>
              <ul className="list-disc ml-6 text-gray-800 text-lg">
                {member.memberships.map((mem, i) => (
                  <li key={i}>{mem}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
